// Pantalla Cobranza (`/credit/collections`): el único componente con estado del módulo. Ensambla el
// selector de cadena, la vista Cartera (carga por cobrador · explorer de asignaciones) y la vista
// Reglas, y es el que dispara las mutaciones — siempre pasando por la confirmación.
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CreateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/CreateAssignmentRuleRequest';
import type { UpdateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/UpdateAssignmentRuleRequest';
import { EmptyState, Tabs } from '@/kit';
import { MasterDetail, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { userMessageOf } from '@/api/errors';
import { useAuth } from '@/hooks/useAuth';
import type { CollectorAssignmentQuery } from '@/api/services/loan-collector-assignment';
import {
    COLLECTIONS_KEY,
    useActivateRule,
    useAssignmentRules,
    useCollectorAssignments,
    useCollectorLoads,
    useCreateRule,
    useRedistributeCollector,
    useReassignAssignment,
    useRetailOrgDirectory,
    useUpdateRule,
} from './api/useCollections';
import {
    activeRule,
    describeCollector,
    describeStrategy,
    filterAssignments,
    listAssignmentCollectors,
    listAssignmentStores,
} from './logic';
import { resolveCollectionsAbilities } from './permissions';
import { resolveRetailerScope } from './retailerScope';
import {
    ANY_FILTER,
    type AssignmentFiltersState,
    type AssignmentRow,
    type CollectorLoadRow,
    type DirectoryPresence,
    type RuleRow,
} from './types';
import { AssignmentDetail } from './components/AssignmentDetail';
import { AssignmentsTable } from './components/AssignmentsTable';
import {
    CollectionsFiltersPanel,
    EMPTY_ASSIGNMENT_FILTERS,
} from './components/CollectionsFiltersPanel';
import { CollectorLoadsStrip } from './components/CollectorLoadsStrip';
import { ConfirmMutationDialog, type MutationRequest } from './components/ConfirmMutationDialog';
import { RetailerPicker } from './components/RetailerPicker';
import { RuleDetail } from './components/RuleDetail';
import { RuleForm, type RuleFormValues } from './components/RuleForm';
import { RulesList } from './components/RulesList';

/** Los filtros los resuelve el cliente: la pantalla necesita el lote completo para armar los combos. */
const NO_SERVER_FILTERS: CollectorAssignmentQuery = {};

type CollectionsView = 'portfolio' | 'rules';

const PORTFOLIO_TAB = { key: 'portfolio', label: 'Cartera' };
const RULES_TAB = { key: 'rules', label: 'Reglas de asignación' };

/** La mutación abierta en el diálogo: el copy que se muestra y el id contra el que se ejecuta. */
interface PendingMutation {
    request: MutationRequest;
    /** El crédito, el cobrador o la regla, según el tipo de mutación. */
    targetId: string;
}

/** El formulario abierto: `rule: null` es crear una regla nueva. */
interface OpenRuleForm {
    rule: RuleRow | null;
    /** Lo ya tecleado, para volver al formulario tal cual si se cancela la confirmación. */
    draft?: RuleFormValues;
}

/** Lo que el formulario quiere guardar, esperando la confirmación de que apaga la regla vigente. */
interface PendingRuleSave {
    values: RuleFormValues;
    rule: RuleRow | null;
}

export interface CollectionsPageProps {
    /** Deep link `?retailer=`: manda sobre la cadena que el rol del usuario permitiría elegir. */
    retailer?: string;
    /** Deep link `?collector=`: abre la lista filtrada por ese cobrador. */
    collector?: string;
    /** Deep link `?credit=`: abre la asignación de ese crédito. */
    credit?: string;
}

export function CollectionsPage({ retailer, collector, credit }: CollectionsPageProps) {
    const { permissions, roleAssignments } = useAuth();
    const queryClient = useQueryClient();
    const abilities = useMemo(() => resolveCollectionsAbilities(permissions), [permissions]);
    const scope = useMemo(() => resolveRetailerScope(roleAssignments), [roleAssignments]);

    const [chosenRetailerId, setChosenRetailerId] = useState('');
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    // El deep link manda sobre el rol: sirve para apuntar a datos de prueba de otra cadena.
    const retailerId = retailer ?? scope.fixedRetailerId ?? chosenRetailerId;
    const hasRetailer = retailerId !== '';

    const [view, setView] = useState<CollectionsView>('portfolio');
    const [filters, setFilters] = useState<AssignmentFiltersState>(() => ({
        ...EMPTY_ASSIGNMENT_FILTERS,
        collectorUserId: collector ?? ANY_FILTER,
    }));
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
    const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
    const [pending, setPending] = useState<PendingMutation | null>(null);
    const [openRuleForm, setOpenRuleForm] = useState<OpenRuleForm | null>(null);
    const [pendingRuleSave, setPendingRuleSave] = useState<PendingRuleSave | null>(null);

    const loads = useCollectorLoads(hasRetailer ? retailerId : undefined);
    const assignments = useCollectorAssignments(
        hasRetailer ? retailerId : undefined,
        NO_SERVER_FILTERS,
    );
    const rules = useAssignmentRules(
        hasRetailer && abilities.canReadRules ? retailerId : undefined,
    );
    const directory = useRetailOrgDirectory(hasRetailer ? retailerId : undefined);

    const reassign = useReassignAssignment(retailerId);
    const redistribute = useRedistributeCollector(retailerId);
    const activate = useActivateRule(retailerId);
    const createRule = useCreateRule(retailerId);
    const updateRule = useUpdateRule(retailerId);

    const visibleRows = useMemo(
        () => filterAssignments(assignments.rows, filters),
        [assignments.rows, filters],
    );
    const stores = useMemo(() => listAssignmentStores(assignments.rows), [assignments.rows]);
    const collectors = useMemo(
        () => listAssignmentCollectors(assignments.rows),
        [assignments.rows],
    );
    // El deep link `?credit=` es el default de la selección, no un efecto que la pise: en cuanto el
    // operador elige una fila, la suya manda.
    const deepLinkedAssignmentId = useMemo(() => {
        const wanted = credit?.trim();
        if (wanted === undefined || wanted === '') return null;
        return assignments.rows.find((row) => row.creditId === wanted)?.assignmentId ?? null;
    }, [assignments.rows, credit]);
    const openAssignmentId = selectedAssignmentId ?? deepLinkedAssignmentId;
    const selectedRow = useMemo(
        () => assignments.rows.find((row) => row.assignmentId === openAssignmentId) ?? null,
        [assignments.rows, openAssignmentId],
    );
    const selectedRule = useMemo(
        () => rules.rules.find((rule) => rule.ruleId === selectedRuleId) ?? null,
        [rules.rules, selectedRuleId],
    );
    const currentRule = useMemo(() => activeRule(rules.rules), [rules.rules]);

    const retryRead = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_KEY] });
    }, [queryClient]);

    /** El nombre del cobrador según el directorio; si no está, su id con la leyenda que corresponda. */
    const labelOfCollector = useCallback(
        (collectorUserId: string) => {
            const user = directory.retailUsersById.get(collectorUserId);
            const presence: DirectoryPresence = directory.unavailable
                ? 'UNAVAILABLE'
                : user === undefined
                  ? 'UNKNOWN'
                  : 'KNOWN';
            return describeCollector(collectorUserId, user?.displayName ?? null, presence).text;
        },
        [directory.retailUsersById, directory.unavailable],
    );

    const toggleCollectorFilter = useCallback((collectorUserId: string) => {
        setFilters((current) => ({
            ...current,
            collectorUserId:
                current.collectorUserId === collectorUserId ? ANY_FILTER : collectorUserId,
        }));
    }, []);

    const openReassign = useCallback(
        (row: AssignmentRow) => {
            reassign.reset();
            setPending({
                request: {
                    kind: 'reassign',
                    creditId: row.creditId,
                    collectorLabel: describeCollector(
                        row.collectorUserId,
                        row.collectorDisplayName,
                        row.collectorPresence,
                    ).text,
                },
                targetId: row.creditId,
            });
        },
        [reassign],
    );

    const openRedistribute = useCallback(
        (row: CollectorLoadRow) => {
            redistribute.reset();
            setPending({
                request: {
                    kind: 'redistribute',
                    collectorLabel: describeCollector(
                        row.collectorUserId,
                        row.displayName,
                        row.presence,
                    ).text,
                    activeAssignments: row.activeAssignments,
                },
                targetId: row.collectorUserId,
            });
        },
        [redistribute],
    );

    const openActivateRule = useCallback(
        (rule: RuleRow) => {
            activate.reset();
            setPending({
                request: {
                    kind: 'activateRule',
                    strategyLabel: describeStrategy(rule.strategy),
                    hasActiveRule: currentRule !== null,
                },
                targetId: rule.ruleId,
            });
        },
        [activate, currentRule],
    );

    // Cerrar el diálogo también limpia el error de la mutación: si no, el siguiente crédito abría
    // con el fallo del anterior.
    const closePending = useCallback(() => {
        const kind = pending?.request.kind;
        setPending(null);
        reassign.reset();
        redistribute.reset();
        activate.reset();
        // Cancelar la confirmación de guardado devuelve al formulario con lo que ya se había tecleado.
        if (kind === 'saveRule' && pendingRuleSave !== null) {
            setOpenRuleForm({ rule: pendingRuleSave.rule, draft: pendingRuleSave.values });
            setPendingRuleSave(null);
        }
    }, [activate, pending, pendingRuleSave, reassign, redistribute]);

    const runReassign = useCallback(
        (creditId: string, reason: string) => {
            reassign.mutate(
                { creditId, reason },
                {
                    onSuccess: (result) => {
                        setPending(null);
                        toast.success('Crédito reasignado', {
                            description: `El crédito ${creditId} pasó a ${labelOfCollector(result.collectorUserId)}.`,
                        });
                    },
                    onError: (error) => {
                        toast.error('No se pudo reasignar', {
                            description: userMessageOf(error),
                        });
                    },
                },
            );
        },
        [labelOfCollector, reassign],
    );

    const runRedistribute = useCallback(
        (collectorUserId: string, reason: string) => {
            const who = labelOfCollector(collectorUserId);
            redistribute.mutate(
                { collectorUserId, reason },
                {
                    onSuccess: (summary) => {
                        setPending(null);
                        const moved = summary.reassignedCount;
                        // Un 200 con `errors` NO es un éxito: parte de la cartera se quedó donde estaba.
                        if (summary.errors.length > 0) {
                            const total = moved + summary.errors.length;
                            const stuck = summary.errors.map((entry) => entry.creditId).join(', ');
                            toast.warning('La redistribución quedó a medias', {
                                description: `Se movieron ${String(moved)} de ${String(total)} asignaciones de ${who}. Siguen con él: ${stuck}. Primer motivo: ${summary.errors[0].reason}`,
                            });
                            return;
                        }
                        if (moved === 0) {
                            toast.info('No se movió nada', {
                                description: `${who} no tenía cartera activa que mover.`,
                            });
                            return;
                        }
                        toast.success('Cartera redistribuida', {
                            description: `Se movieron ${String(moved)} asignaciones de ${who}.`,
                        });
                    },
                    onError: (error) => {
                        toast.error('No se pudo redistribuir', {
                            description: userMessageOf(error),
                        });
                    },
                },
            );
        },
        [labelOfCollector, redistribute],
    );

    const runActivateRule = useCallback(
        (ruleId: string) => {
            activate.mutate(
                { ruleId },
                {
                    onSuccess: (rule) => {
                        setPending(null);
                        toast.success('Regla activada', {
                            description: `El motor asigna con ${describeStrategy(rule.strategy)}.`,
                        });
                    },
                    onError: (error) => {
                        toast.error('No se pudo activar la regla', {
                            description: userMessageOf(error),
                        });
                    },
                },
            );
        },
        [activate],
    );

    const runSaveRule = useCallback(
        (values: RuleFormValues, editing: RuleRow | null) => {
            const onSuccess = () => {
                setOpenRuleForm(null);
                setPendingRuleSave(null);
                setPending(null);
                toast.success(editing ? 'Regla actualizada' : 'Regla creada', {
                    description: `El motor la evalúa con ${describeStrategy(values.strategy)}.`,
                });
            };
            const onError = (error: Error) => {
                toast.error(
                    editing ? 'No se pudo actualizar la regla' : 'No se pudo crear la regla',
                    {
                        description: userMessageOf(error),
                    },
                );
            };
            if (editing) {
                const body: UpdateAssignmentRuleRequest = {
                    strategy: values.strategy,
                    maxLoadPerCollector: values.maxLoadPerCollector,
                    priorityWeights: values.priorityWeights,
                    active: values.active,
                };
                updateRule.mutate({ ruleId: editing.ruleId, body }, { onSuccess, onError });
                return;
            }
            const body: CreateAssignmentRuleRequest = {
                strategy: values.strategy,
                maxLoadPerCollector: values.maxLoadPerCollector,
                priorityWeights: values.priorityWeights,
                active: values.active,
            };
            createRule.mutate(body, { onSuccess, onError });
        },
        [createRule, updateRule],
    );

    const confirmPending = useCallback(
        (reason: string) => {
            if (!pending) return;
            switch (pending.request.kind) {
                case 'reassign':
                    runReassign(pending.targetId, reason);
                    return;
                case 'redistribute':
                    runRedistribute(pending.targetId, reason);
                    return;
                case 'activateRule':
                    runActivateRule(pending.targetId);
                    return;
                case 'saveRule':
                    if (pendingRuleSave) runSaveRule(pendingRuleSave.values, pendingRuleSave.rule);
                    return;
            }
        },
        [pending, pendingRuleSave, runActivateRule, runReassign, runRedistribute, runSaveRule],
    );

    /**
     * Guardar una regla como activa apaga la vigente igual que el botón «Activar»: mismo efecto,
     * misma confirmación. Sin regla vigente, o editando la que ya lo está, no hay nada que apagar.
     */
    const submitRuleForm = useCallback(
        (values: RuleFormValues) => {
            const editing = openRuleForm?.rule ?? null;
            const deactivating =
                values.active && currentRule !== null && currentRule.ruleId !== editing?.ruleId
                    ? currentRule
                    : null;
            if (deactivating === null) {
                runSaveRule(values, editing);
                return;
            }
            setOpenRuleForm(null);
            setPendingRuleSave({ values, rule: editing });
            setPending({
                request: {
                    kind: 'saveRule',
                    strategyLabel: describeStrategy(values.strategy),
                    deactivatingLabel: describeStrategy(deactivating.strategy),
                    isEdit: editing !== null,
                },
                targetId: editing?.ruleId ?? '',
            });
        },
        [currentRule, openRuleForm, runSaveRule],
    );

    if (!abilities.canReadAssignments) {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-muted-foreground">
                No tienes permiso para ver la cartera de cobranza. Si crees que es un error,
                contacta al administrador.
            </div>
        );
    }

    const tabs = abilities.canReadRules ? [PORTFOLIO_TAB, RULES_TAB] : [PORTFOLIO_TAB];
    const activeView: CollectionsView = abilities.canReadRules ? view : 'portfolio';
    const mutationIsPending =
        reassign.isPending ||
        redistribute.isPending ||
        activate.isPending ||
        createRule.isPending ||
        updateRule.isPending;
    // El error se muestra DENTRO del diálogo, que queda abierto: el intento se puede repetir sin
    // volver a elegir la fila ni a escribir el motivo.
    const pendingError =
        pending === null
            ? null
            : pending.request.kind === 'reassign'
              ? reassign.error
              : pending.request.kind === 'redistribute'
                ? redistribute.error
                : pending.request.kind === 'saveRule'
                  ? (updateRule.error ?? createRule.error)
                  : activate.error;

    return (
        <ScreenFrame>
            <div className="flex flex-none flex-wrap items-center gap-3 border-b border-border bg-card px-3 py-2">
                <div className="w-56">
                    <RetailerPicker
                        value={chosenRetailerId}
                        onChange={setChosenRetailerId}
                        pinnedRetailerId={retailer}
                    />
                </div>
                <Tabs
                    className="border-b-0"
                    tabs={tabs}
                    activeTab={activeView}
                    onChange={(key) => {
                        setView(key === 'rules' ? 'rules' : 'portfolio');
                    }}
                />
            </div>

            {!hasRetailer ? (
                <div className="p-6">
                    <EmptyState
                        title="Elige una cadena"
                        description="La cartera de cobranza se administra por cadena. Elige una arriba para ver sus cobradores y sus asignaciones."
                    />
                </div>
            ) : activeView === 'rules' ? (
                <MasterDetail
                    className="m-2 gap-2 rounded-[10px] border border-border"
                    list={
                        <RulesList
                            rules={rules.rules}
                            isLoading={rules.isLoading}
                            error={rules.error}
                            onRetry={retryRead}
                            selectedRuleId={selectedRuleId}
                            onSelect={setSelectedRuleId}
                            canCreateRule={abilities.canCreateRule}
                            onCreate={() => {
                                createRule.reset();
                                updateRule.reset();
                                setOpenRuleForm({ rule: null });
                            }}
                        />
                    }
                    detail={
                        <RuleDetail
                            rule={selectedRule}
                            canUpdateRule={abilities.canUpdateRule}
                            onActivate={openActivateRule}
                            onEdit={(rule) => {
                                createRule.reset();
                                updateRule.reset();
                                setOpenRuleForm({ rule });
                            }}
                        />
                    }
                />
            ) : (
                <>
                    {loads.directoryUnavailable || assignments.directoryUnavailable ? (
                        <div
                            role="status"
                            className="flex-none border-b border-warning/40 bg-warning/10 px-3 py-2 text-[11.5px] text-foreground"
                        >
                            <strong className="font-bold">
                                No se pudieron resolver los nombres.
                            </strong>{' '}
                            El directorio de la cadena no respondió, así que donde iría un nombre
                            ves el identificador. Los números de carga sí son buenos.
                        </div>
                    ) : null}

                    <div className="flex-none border-b border-border p-2">
                        <CollectorLoadsStrip
                            rows={loads.rows}
                            isLoading={loads.isLoading}
                            error={loads.error}
                            onRetry={retryRead}
                            selectedCollectorUserId={
                                filters.collectorUserId === ANY_FILTER
                                    ? null
                                    : filters.collectorUserId
                            }
                            onSelect={toggleCollectorFilter}
                            canRedistribute={abilities.canRedistribute}
                            onRedistribute={openRedistribute}
                        />
                    </div>

                    <MasterDetailExplorer
                        filtersCollapsed={filtersCollapsed}
                        filters={
                            <CollectionsFiltersPanel
                                filters={filters}
                                onChange={setFilters}
                                stores={stores}
                                collectors={collectors}
                                collapsed={filtersCollapsed}
                                onToggleCollapsed={() => {
                                    setFiltersCollapsed((prev) => !prev);
                                }}
                            />
                        }
                        list={
                            <AssignmentsTable
                                rows={visibleRows}
                                totalRows={assignments.rows.length}
                                isLoading={assignments.isLoading}
                                error={assignments.error}
                                onRetry={retryRead}
                                selectedAssignmentId={openAssignmentId}
                                onSelect={setSelectedAssignmentId}
                            />
                        }
                        detail={
                            <AssignmentDetail
                                row={selectedRow}
                                canReassign={abilities.canReassign}
                                onReassign={openReassign}
                            />
                        }
                    />
                </>
            )}

            {pending ? (
                <ConfirmMutationDialog
                    open
                    request={pending.request}
                    isPending={mutationIsPending}
                    error={pendingError}
                    onCancel={closePending}
                    onConfirm={confirmPending}
                />
            ) : null}

            {openRuleForm ? (
                <RuleForm
                    key={openRuleForm.rule?.ruleId ?? 'new'}
                    open
                    rule={openRuleForm.rule}
                    draft={openRuleForm.draft}
                    isSaving={createRule.isPending || updateRule.isPending}
                    error={openRuleForm.rule ? updateRule.error : createRule.error}
                    onCancel={() => {
                        setOpenRuleForm(null);
                        setPendingRuleSave(null);
                        createRule.reset();
                        updateRule.reset();
                    }}
                    onSubmit={submitRuleForm}
                />
            ) : null}
        </ScreenFrame>
    );
}
